import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  investors as investorApi,
  portfolio as portfolioApi,
  orders as orderApi,
  type Account,
  type Instrument,
  type Investor,
  type OrderSide,
  type Position,
  type QuantityMode,
} from "../../api";
import { Link, useRouter } from "../../router";

const SIDES: { value: OrderSide; label: string }[] = [
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
  { value: "switch", label: "Switch" },
];

const MODES: { value: QuantityMode; label: string }[] = [
  { value: "units", label: "Units" },
  { value: "amount", label: "Amount" },
  { value: "percent", label: "Percent" },
];

// Prefill account/investor from the query string (e.g. linked from a portfolio
// account page). Falls back to empty selects.
function initialIds(): { investor: string; account: string } {
  const q = new URLSearchParams(window.location.search);
  return { investor: q.get("investor") ?? "", account: q.get("account") ?? "" };
}

export function OrderForm() {
  const { navigate } = useRouter();
  const prefill = useMemo(initialIds, []);

  const [investors, setInvestors] = useState<Investor[]>([]);
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);

  const [investorId, setInvestorId] = useState(prefill.investor);
  const [accountId, setAccountId] = useState(prefill.account);
  const [side, setSide] = useState<OrderSide>("buy");
  const [isin, setIsin] = useState("");
  const [toIsin, setToIsin] = useState("");
  const [mode, setMode] = useState<QuantityMode>("amount");
  const [quantity, setQuantity] = useState("");
  const [limitPrice, setLimitPrice] = useState("");
  const [note, setNote] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Investors + instrument catalog up front.
  useEffect(() => {
    Promise.all([investorApi.list(), portfolioApi.instruments()])
      .then(([inv, ins]) => {
        setInvestors(inv.investors);
        setInstruments(ins.instruments);
      })
      .catch((e) => setError(e.message));
  }, []);

  // Accounts follow the chosen investor.
  useEffect(() => {
    if (!investorId) {
      setAccounts([]);
      return;
    }
    let cancelled = false;
    portfolioApi
      .accountsForInvestor(Number(investorId))
      .then((d) => {
        if (cancelled) return;
        setAccounts(d.accounts);
        // Drop a stale account selection that doesn't belong to this investor.
        if (!d.accounts.some((a) => String(a.id) === accountId)) setAccountId("");
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorId]);

  // Held positions follow the chosen account (the sell/switch-from universe).
  useEffect(() => {
    if (!accountId) {
      setPositions([]);
      return;
    }
    let cancelled = false;
    portfolioApi
      .positions(Number(accountId))
      .then((d) => !cancelled && setPositions(d.positions))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const account = accounts.find((a) => String(a.id) === accountId) ?? null;
  const currency = account?.currency ?? "";

  // The primary-instrument options depend on the side: buys come from the
  // catalog; sells/switches must start from something actually held.
  const primaryOptions =
    side === "buy"
      ? instruments.map((i) => ({ isin: i.isin, name: i.name }))
      : positions.map((p) => ({ isin: p.isin, name: p.name }));

  const quantityHint = (() => {
    if (mode === "units") return "Number of units";
    if (mode === "amount") return currency ? `Amount in ${currency}` : "Amount";
    return side === "buy" ? "% of account value" : "% of the current holding";
  })();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!accountId) return setError("Pick an account.");
    if (!isin) return setError("Pick an instrument.");
    if (side === "switch" && !toIsin) return setError("Pick a target instrument.");
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      return setError("Quantity must be a positive number.");
    }
    setSubmitting(true);
    try {
      const { order } = await orderApi.create({
        account_id: Number(accountId),
        side,
        isin,
        to_isin: side === "switch" ? toIsin : null,
        quantity_mode: mode,
        quantity: qty,
        limit_price: limitPrice ? Number(limitPrice) : null,
        note: note.trim() || null,
      });
      navigate(`/app/orders/${order.id}`);
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <Link to="/app/orders" className="back-link">
        ← Cancel
      </Link>
      <header className="page-head">
        <div>
          <span className="eyebrow">Orders</span>
          <h1>New order</h1>
        </div>
      </header>

      <form className="card-form" onSubmit={onSubmit} noValidate>
        <div className="field-row">
          <label className="field field-grow">
            <span>Investor *</span>
            <select
              value={investorId}
              onChange={(e) => setInvestorId(e.target.value)}
              required
            >
              <option value="">Select investor…</option>
              {investors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field field-grow">
            <span>Account *</span>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              disabled={!investorId}
              required
            >
              <option value="">
                {investorId ? "Select account…" : "Pick an investor first"}
              </option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.currency})
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span>Side *</span>
          <div className="seg">
            {SIDES.map((s) => (
              <button
                key={s.value}
                type="button"
                className={side === s.value ? "seg-option active" : "seg-option"}
                onClick={() => {
                  setSide(s.value);
                  // Held-instrument selections don't carry across a side change.
                  setIsin("");
                  setToIsin("");
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </label>

        <label className="field">
          <span>{side === "switch" ? "Sell from *" : "Instrument *"}</span>
          <select
            value={isin}
            onChange={(e) => setIsin(e.target.value)}
            disabled={!accountId}
            required
          >
            <option value="">
              {side === "buy"
                ? "Select instrument…"
                : accountId
                  ? "Select a held instrument…"
                  : "Pick an account first"}
            </option>
            {primaryOptions.map((o) => (
              <option key={o.isin} value={o.isin}>
                {o.name} — {o.isin}
              </option>
            ))}
          </select>
          {side !== "buy" && accountId && primaryOptions.length === 0 && (
            <span className="field-hint">This account holds no positions yet.</span>
          )}
        </label>

        {side === "switch" && (
          <label className="field">
            <span>Switch into *</span>
            <select
              value={toIsin}
              onChange={(e) => setToIsin(e.target.value)}
              required
            >
              <option value="">Select target instrument…</option>
              {instruments
                .filter((i) => i.isin !== isin)
                .map((i) => (
                  <option key={i.isin} value={i.isin}>
                    {i.name} — {i.isin}
                  </option>
                ))}
            </select>
          </label>
        )}

        <div className="field-row">
          <label className="field field-grow">
            <span>Quantity mode *</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as QuantityMode)}
            >
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field field-grow">
            <span>Quantity *</span>
            <input
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
            />
            <span className="field-hint">{quantityHint}</span>
          </label>
        </div>

        <label className="field">
          <span>Execution price (optional)</span>
          <input
            type="number"
            min="0"
            step="any"
            value={limitPrice}
            onChange={(e) => setLimitPrice(e.target.value)}
            placeholder="Defaults to the latest known price"
          />
          <span className="field-hint">
            Used to value the order when no daily price is on file.
          </span>
        </label>

        <label className="field">
          <span>Note (optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        {error && <div className="banner-error">{error}</div>}

        <div className="form-actions">
          <Link to="/app/orders" className="btn btn-ghost">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Creating…" : "Create order"}
          </button>
        </div>
      </form>
    </div>
  );
}
